import { withTranslation } from "react-i18next";
import WithRouter from "../../../../../components/Common/WithRouter";
import React, { useRef, useState } from "react";
import { request } from "../../../../../utils/Request";
import { ConfirmPopup } from 'primereact/confirmpopup';
import StateLessGrid from "../../../../../components/Shared/StateLessGrid";
import QuickCopyDropdown from "../../../../../components/Shared/QuickCopyDropdown";
import { Button } from "primereact/button";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { InputField } from "../../../../../components/Shared/InputField";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { find, get, isEmpty } from "lodash";
import Tooltip from "../../../../../components/Common/Display/Tooltip";
import HasAccess from "../../../../../utils/HasAccess";
import { ButtonGroup } from "primereact/buttongroup";
import AlpOutboundLoadDetail from "../../../alpOutboundLoads/AlpOutboundLoadDetail";
import AlpOutboundLoadUpdatePrice from "../../../alpOutboundLoads/partials/AlpOutboundLoadUpdatePrice";
import AlpOutboundLoadExcel from "./AlpOutboundLoadExcel";
import AlpOutboundLoadFilter from "./AlpOutboundLoadFilter";
import { navigateBackToList } from "../../../../../utils/listNavigation";
import LoadComments from "../../../loads/partials/LoadComments";
import Location from "../../../../../components/Common/Display/Location";
import VinWithType from "../../../../../components/Common/Display/VinWithType";
import { utcDateToLocalDate } from "../../../../../utils/Helper";
import AlpOutboundLoadStatus from "../../../alpOutboundLoads/partials/AlpOutboundLoadStatus";
import { LoadStatusFlagChip } from "../../../loads/partials/LoadStatusChip";

const AlpOutboundLoadList = (props: any) => {
    const { t, moduleState, toastify, dropdowns } = props;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [operation, setOperation] = useState<any>(searchParams.get('operation') ?? null);
    const [operationId, setOperationId] = useState<any>(searchParams.get('operationId') ?? null);
    const [filterPanel, setFilterPanel] = useState<boolean>(false);
    const [excelPanel, setExcelPanel] = useState<boolean>(false);
    const [loadId, setLoadId] = useState<any>(null);
    const [commentLoad, setCommentLoad] = useState<any>(null);
    const [total, setTotal] = useState(0);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [clickCount, setClickCount] = useState(0);
    const [visibleSpecialAction, setVisibleSpecialAction] = useState(false);
    const [disableSpecialAction, setDisableSpecialAction] = useState(false);

    const [initSearchFormState, setInitSearchFormState] = useState<any>({
        search: moduleState.search ?? null
    });
    const tableRef: any = useRef();

    const refreshTable = () => {
        if ( tableRef.current ) {
            tableRef.current.onRefresh();
        }
    };

    const onOpenLoadComments = (row: any) => {
        setCommentLoad({
            type: "AlpOutboundLoad",
            load_source_id: row.source_id || row.id,
            load_number: row.load_number,
            source_id: row.source_id,
            id: row.id,
            load_comments_count: row.load_comments_count,
        });
    };

    const onSync = async (id: string) => {
        try {
            await request({
                url: `/suppliers/alp/outbound/loads/${ id }/sync`,
                method: 'POST',
            });

            toastify(t("record updated", { item: t('alp outbound load') + ' ' + t('outbound load') }), "info");
            refreshTable();
        } catch (e: any) {
            if ( e.status === 422 ) {
                toastify(e.data.errors.driver_id.join(", "), "error");
            } else if ( e.statusText ) {
                toastify(e.statusText, "error");
            }
        }
    };

    const columns = [
        {
            field: 'load_number',
            title: t("load number"),
            sorting: true,
            render: ({ load_number }: any) => <strong>{ load_number || '' }</strong>,
        },
        {
            field: 'driver_name',
            title: t('driver'),
            sorting: true,
        },
        { field: 'cmr', title: t('cmr'), sorting: true },
        {
            field: 'legal_entity',
            title: t('legal entity'),
            sorting: false,
            render: (row: any) => {
                const label = get(row, ['legal_entity', 'company_name'])
                    || get(find(dropdowns?.legalEntities || [], { id: get(row, ['legal_entity', 'id']) }), ['label']);
                return label || '-';
            },
        },
        {
            field: 'client',
            title: t('client'),
            sorting: false,
            render: (row: any) => get(row, ['client', 'name']) || '-',
        },
        {
            field: 'provider',
            title: t('provider'),
            sorting: false,
            render: (_row: any) => {
                const providerSlug = get(_row, ['provider', 'slug']) || get(_row, ['source_type']);
                const provider = find(dropdowns?.providers || [], { id: providerSlug });
                return get(_row, ['provider', 'title'])
                    || get(provider, ['label'])
                    || providerSlug
                    || '-';
            },
        },
        {
            field: 'model',
            title: t('model(s)'),
            sorting: false,
            render: ({ alp_outbound_load_orders }: any) => (alp_outbound_load_orders || [])
                .map(({ order }: any, index: number) => (
                    <div key={ index }>
                        { get(order, ['brand_model', 'title']) } - { get(order, ['brand_model', 'version_code']) }
                    </div>
                )),
        },
        {
            field: 'vin_number',
            title: t('vin(s)'),
            sorting: false,
            render: ({ alp_outbound_load_orders }: any) => (alp_outbound_load_orders || [])
                .map(({ order }: any, index: number) => (
                    <div key={ index }>
                        <VinWithType vinNumber={ get(order, ['vin_number']) } />
                    </div>
                )),
        },
        {
            field: 'truck_plate_number',
            title: t('transport vehicle'),
            sorting: false,
            render: ({ truck_plate_number, trailer_plate_number, transport_vehicle }: any) => (
                <>
                    <span>{ !isEmpty(truck_plate_number) ? truck_plate_number : get(transport_vehicle, ['truck_plate_number']) || '' }</span>
                    <span>{ !isEmpty(trailer_plate_number) ? ` | ${ trailer_plate_number }` : (get(transport_vehicle, ['trailer_plate_number']) ? ` | ${ get(transport_vehicle, ['trailer_plate_number']) }` : '') }</span>
                </>
            ),
        },
        {
            field: 'loading',
            title: t('location'),
            sorting: false,
            render: ({ from_location, to_location }: any) => (
                <>
                    <Location item={ from_location } titled={ 1 } />
                    <Location item={ to_location } titled={ 1 } />
                </>
            ),
        },
        {
            field: 'qty',
            title: t('qty'),
            sorting: true,
        },
        {
            field: 'status',
            title: t('status'),
            sorting: true,
            render: ({ status, gate_out_confirmed_at, invoiced_at }: any) => (
                <div className="d-flex flex-column gap-1">
                    <div>
                        <AlpOutboundLoadStatus status={ status } chip={ true } />
                    </div>
                    { status !== 'announced' && isEmpty(gate_out_confirmed_at) &&
                      <LoadStatusFlagChip colorClass="text-red-500" label={ t('gate out: pending') } />
                    }
                    { !isEmpty(invoiced_at) &&
                      <LoadStatusFlagChip colorClass="text-green-500" label={ t('invoiced') } />
                    }
                </div>
            ),
        },
        {
            field: 'actual_loading_time',
            title: t('in transit'),
            sorting: false,
            render: ({ actual_loading_time }: any) => (
                actual_loading_time ? <span>{ utcDateToLocalDate(actual_loading_time) }</span> : null
            ),
        },
        {
            field: 'actual_unloading_time',
            title: t('delivered at'),
            sorting: false,
            render: ({ actual_unloading_time }: any) => (
                actual_unloading_time ? <span>{ utcDateToLocalDate(actual_unloading_time) }</span> : null
            ),
        },
        {
            field: 'invoiced_at',
            title: t('invoiced at'),
            sorting: false,
            render: ({ invoiced_at }: any) => (
                invoiced_at ? <span>{ utcDateToLocalDate(invoiced_at) }</span> : null
            ),
        },
        {
            field: 'invoice_number',
            title: t('invoice number'),
            sorting: false,
            render: ({ invoice_number }: any) => invoice_number ?? '',
        },
        {
            field: 'comments',
            title: t('comments'),
            sorting: false,
            align: 'center',
            render: (row: any) => {
                if ( !(row.source_id || row.id) ) {
                    return null;
                }

                return (
                    <HasAccess hasAnyAccess={ ['loadcommentsview', 'loadcommentsstore'] }>
                        {/*@ts-ignore*/}
                        <Tooltip tooltip={ Number(row?.load_comments_count || 0) > 0 ? `${ t('comments') } (${ row.load_comments_count })` : t('comments') }>
                            <a onClick={ () => onOpenLoadComments(row) }
                               className={ "cursor-pointer ms-1 text-blue-500 load-comment-action" }>
                                <i className="fas fa-comments" />
                                { Number(row?.load_comments_count || 0) > 0 &&
                                  <span className="load-comment-action__count">{ row.load_comments_count }</span>
                                }
                            </a>
                        </Tooltip>
                    </HasAccess>
                );
            },
        },
        {
            field: 'action',
            title: t('action'),
            sorting: false,
            align: 'left',
            render: (_row: any) => {
                return (
                    <>
                        <HasAccess hasAllAccess={ ['alpoutboundloadsupdate'] }>
                            <Tooltip tooltip={ t('sync from alp') }>
                                <a onClick={ () => onSync(_row.id) }
                                   className={ "cursor-pointer" }>
                                    <i className="fas fa-sync" />
                                </a>
                            </Tooltip>
                            <Tooltip tooltip={ t('edit item') }>
                                <a onClick={ () => setLoadId(_row.id) } className={ "ms-2" }>
                                    <i className="fas fa-dollar-sign" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAnyAccess={ ['alpoutboundloadsview'] }>
                            <Tooltip tooltip={ t('detail of item') }>
                                <a
                                    onClick={ () => onOperationClick('detail', _row.id, 'archived/alp/outbound/loads') }
                                    className={ 'ms-2' }
                                >
                                    <i className="fas fa-eye" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                    </>
                );
            }
        },
    ];

    const onOperationClick = (operationToBePerformed: any = 'add', id: any = null, backTo: any = null) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('operation', operationToBePerformed);
        searchParams.set('operationId', id);

        if ( !isEmpty(backTo) ) {
            // @ts-ignore
            searchParams.set('back_to', backTo);
        }

        if ( isEmpty(operationToBePerformed) ) {
            searchParams.delete('operation');
        }

        if ( isEmpty(id) ) {
            searchParams.delete('operationId');
        }

        navigate({
            pathname: location.pathname,
            search: searchParams.toString(),
        }, { replace: true });
        setOperationId(id);
        setOperation(operationToBePerformed);
    };

    const onReset = (reset: any = true) => {
        const mStates = { ...moduleState };

        if ( reset ) {
            mStates.filters = [];
            mStates.page = 1;
            mStates.search = null;
        }

        props.setQuery(mStates);
    };

    const onCloseOperation = (reset: any = true) => {
        if ( !isEmpty(searchParams.get('back_to'))) {
            navigateBackToList(props.router.navigate, `${ searchParams.get('back_to') }`);
            return;
        }

        onOperationClick(null, null);
        onReset(reset);
    };

    const onSearch = ({ search }: any) => {
        props.setQuery({
            ...moduleState,
            search: search
        });
    };

    const onFilterClick = () => {
        setFilterPanel(true);
    };

    const onExcelClick = () => {
        setExcelPanel(true);
    };

    const onSyncList = async () => {
        try {
            await request({
                url: `/suppliers/provider/outbound/loads/sync/list`,
                method: 'POST',
                data: {
                    archived: true
                }
            });

            onReset();
        } catch (e: any) {
            if ( e.status === 422 ) {
                return toastify(e.data.message, "error");
            }

            toastify(t("server error"), "error");
        } finally {
            setDisableSpecialAction(true);
        }
    };

    const handleHeaderClick = () => {
        const newCount = clickCount + 1;
        setClickCount(newCount);

        if ( newCount >= 5 && !visibleSpecialAction ) {
            setVisibleSpecialAction(true);
        }
    };

    const focusOnSearch = () => {
        const searchInput: HTMLInputElement | null = document.querySelector('input[name="search"]');
        if ( searchInput ) {
            searchInput.focus();
        }
    };

    return (
        <div className={ "block-viewer" }>
            <div className="block-section">
                <div className="block-header">
                    <span className="block-title">
                        <h5 className={ "mb-0" }>
                            <span onClick={ handleHeaderClick }>
                                { t('EF') + ' ' + t('outbound load') } ({total})
                            </span>
                        </h5>
                    </span>
                    <div className="block-actions">
                        {
                            operation == 'detail' &&
                            <div>
                                <Button type={ "button" }
                                        tooltip={ t("operation cancel") } className={ "ms-2" }
                                        size={ "small" } severity={ "warning" }
                                        tooltipOptions={ { position: 'top' } } onClick={ () => onCloseOperation(false) }>
                                    <i className="pi pi-arrow-left me-2" /> { t("cancel") }

                                </Button>
                            </div>
                        }
                    </div>
                </div>
                <div className="block-content">
                    {
                        operation !== 'detail' &&
                        <Row className={ "p-2" }>
                            <Col
                              sm={ 12 } md={ 6 } className={ "mb-2" }>
                                <Formik
                                  enableReinitialize={ true }
                                  initialValues={ initSearchFormState }
                                  onSubmit={ onSearch }
                                >
                                    { (formikProps: FormikProps<any>) => {
                                        return (
                                            <Form onSubmit={ formikProps.handleSubmit }>
                                                <Col sm={ 12 } md={ 8 }>
                                                    <Field component={ InputField } withoutLabel={ true }
                                                           autoFocus={ true }
                                                           name="search"
                                                           formGroupClass={ "mb-0" }
                                                           placeholder={ t("search") } />
                                                </Col>
                                            </Form>
                                        );
                                    } }
                                </Formik>
                            </Col>

                            <Col
                              sm={ 12 } md={ 6 } className={ "text-right mb-2" }>
                                <div className="d-inline-block me-2 align-middle text-left">
                                    <QuickCopyDropdown
                                        columns={ columns }
                                        tableName={ "AlpOutboundLoadList" }
                                        selectedRows={ selectedRows }
                                        trackBy={ "id" }
                                        // @ts-ignore
                                        entities={ tableRef.current?.getEntities?.() || [] }
                                    />
                                </div>
                                <ButtonGroup>
                                    {
                                        ( visibleSpecialAction && !disableSpecialAction ) &&
                                        <Button onClick={ onSyncList } icon="pi pi-sync" size={ "small" } outlined
                                                tooltip={ t("sync") + ' ' + t("alp") + ' ' + t("outbound load") }
                                                severity={ "danger" }
                                                tooltipOptions={ { position: 'top' } } />
                                    }
                                    <Button icon="pi pi-filter-fill" size={ "small" } outlined tooltip={ t('filter') }
                                            onClick={ onFilterClick }
                                            tooltipOptions={ { position: 'top' } } />
                                    <Button icon="pi pi-file-excel" size={ "small" } outlined tooltip={ t('excel') }
                                            onClick={ onExcelClick }
                                            tooltipOptions={ { position: 'top' } } />
                                    <Button onClick={ onReset } icon="pi pi-refresh" size={ "small" } outlined
                                            tooltip={ t('reset') }
                                            tooltipOptions={ { position: 'top' } } />
                                </ButtonGroup>
                            </Col>

                            <Col sm={ 12 }>
                                <StateLessGrid
                                  size={ "normal" }
                                  url={ '/suppliers/provider/outbound/loads/archived/list?1=1' }
                                  name={ "AlpOutboundLoadList" }
                                  columns={ columns }
                                  moduleState={ moduleState }
                                  setQuery={ props.setQuery }
                                  ref={ tableRef }
                                  itemSize={ 45 }
                                  setTotal={ setTotal }
                                  stickyHeader={ true }
                                  showQuickCopy={ false }
                                  showLoader={ true }
                                  loaderLabel={ t('loading') }
                                  onRowSelection={ setSelectedRows }
                                  selectedRows={ selectedRows }
                                />
                            </Col>
                        </Row>
                    }

                    {
                        ( operation === 'detail' && !isEmpty(operationId) ) &&
                        <>
                            <AlpOutboundLoadDetail
                              setQuery={ props.setQuery }
                              toastify={ props.toastify }
                              dropdowns={ props.dropdowns }
                              params={ { operation, operationId } }
                              onClose={ onCloseOperation }
                            />
                        </>
                    }

                    <AlpOutboundLoadUpdatePrice
                        toastify={ toastify }
                        dropdowns={ [] }
                        id={ loadId }
                        onClose={ () => {
                            setLoadId(null);
                            onReset();
                        } }
                    />

                    <LoadComments
                        id={ commentLoad ? (commentLoad.load_source_id || commentLoad.source_id || commentLoad.id) : null }
                        load={ commentLoad }
                        toastify={ props.toastify }
                        onRefresh={ refreshTable }
                        onClose={ () => {
                            setCommentLoad(null);
                            focusOnSearch();
                        } } />

                    <AlpOutboundLoadFilter
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        visibility={ filterPanel }
                        onClose={ () => {
                            setFilterPanel(false);
                            focusOnSearch();
                        } }
                    />

                    <AlpOutboundLoadExcel
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        visibility={ excelPanel }
                        onClose={ () => {
                            setExcelPanel(false);
                            focusOnSearch();
                        } }
                    />

                    <ConfirmPopup />
                </div>
            </div>
        </div>
    );
};

AlpOutboundLoadList.propTypes = {
    // t: PropTypes.any,
};

export default withTranslation()(WithRouter(AlpOutboundLoadList));
